import { FC, useMemo, useState } from 'react';
import { Edit, Close } from '@mui/icons-material';
import { useQuery } from 'react-query';
import { Table, Pagination, MobileTable } from '../../../../../components';
import { Button, Box, Typography, useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import {
  ISortDirection,
  IResponse,
  IFieldWalkthroughManagementLocation,
  ReactSetState
} from '../../../../../models';
import { getFieldWalkthroughManagementLocations } from '../../../../../fetch/field-walkthrough-location-management';
import { ListManagementFilters } from './list-management-filters';
import { AddLocationModal } from './add-location-modal';
import { formatDate } from '../../../../../helpers';

interface IFWTListManagement {
  isAddModalOpen: boolean;
  setIsAddModalOpen: ReactSetState<boolean>;
}

export const FWTListManagement: FC<IFWTListManagement> = ({
  isAddModalOpen,
  setIsAddModalOpen
}) => {
  const history = useHistory();
  const [page, setPage] = useState<number>(0);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchedValue, setSearchedValue] = useState<string>('');
  const [showOnlyActive, setShowOnlyActive] = useState<boolean>(true);
  const isMobile = useMediaQuery('(max-width: 960px)');

  const [filters, setFilters] = useState<{
    query?: string;
  } | null>({});
  const [selectedSort, setSelectedSort] = useState<string>('Name');
  const [sortDirection, setSortDirection] = useState<{
    Name?: ISortDirection;
    UpdatedDate?: ISortDirection;
    Active?: ISortDirection;
  }>({
    Name: 'Asc'
  });
  const {
    isLoading,
    data: clientsData,
    refetch: fetchFieldWalkthroughManagementLocations,
    isRefetching
  } = useQuery<IResponse<IFieldWalkthroughManagementLocation[]>, Error>(
    [
      'FieldWalkthroughLocations',
      page,
      perPage,
      filters,
      showOnlyActive,
      selectedSort,
      sortDirection
    ],
    () =>
      getFieldWalkthroughManagementLocations({
        page: page + 1,
        perPage,
        name: filters?.query,
        showOnlyActive: showOnlyActive,
        sortBy: selectedSort,
        sortDirection: sortDirection[selectedSort] as string
      }),
    {
      onSuccess: data => {
        setRecordCount(data.totalRecordCount);
      },
      notifyOnChangeProps: 'tracked'
    }
  );

  const data = useMemo(() => clientsData?.records ?? [], [clientsData]);
  const columns = useMemo(() => {
    const handleClickColumn = (column: string) => {
      setSelectedSort(column);
      setSortDirection({
        ...sortDirection,
        [column]: sortDirection[column] === 'Asc' ? 'Desc' : 'Asc'
      });
    };
    return [
      {
        Header: 'Location Name',
        accessor: 'name',
        isServerSorted: selectedSort === 'Name',
        isServerSortedDesc: sortDirection.Name === 'Desc',
        handleClickColumn: () => handleClickColumn('Name')
      },
      {
        Header: 'Last Updated',
        accessor: 'updatedDate',
        isServerSorted: selectedSort === 'UpdatedDate',
        isServerSortedDesc: sortDirection.UpdatedDate === 'Desc',
        handleClickColumn: () => handleClickColumn('UpdatedDate'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFieldWalkthroughManagementLocation } };
        }) => <>{original.updatedDate ? formatDate(original.updatedDate) : 'No Update Date'}</>
      },
      {
        Header: 'Active',
        accessor: 'isActive',
        isServerSorted: selectedSort === 'Active',
        isServerSortedDesc: sortDirection.Active === 'Desc',
        handleClickColumn: () => handleClickColumn('Active'),
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFieldWalkthroughManagementLocation } };
        }) => <>{original.isActive ? 'Active' : 'Not Active'}</>
      },

      {
        id: 'actions',
        accessor: '',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original }
          }
        }: {
          cell: { row: { original: IFieldWalkthroughManagementLocation } };
        }) => {
          return (
            <Box display="flex">
              <Button
                id="edit-county"
                type="button"
                color="primary"
                startIcon={<Edit />}
                onClick={() => {
                  history.push(
                    `/production/field-walkthroughs/list-management/${original.locationId}`
                  );
                }}
              >
                Edit
              </Button>
            </Box>
          );
        }
      }
    ];
  }, [sortDirection, selectedSort, history]);
  return (
    <>
      <ListManagementFilters
        isLoading={isLoading || isRefetching}
        setSearchValue={setSearchValue}
        setSearchedValue={setSearchedValue}
        searchValue={searchValue}
        setIsActive={val => {
          setShowOnlyActive(val);
          setPage(0);
        }}
        isActive={showOnlyActive}
        handleSearch={(val: string) => {
          setPage(0);
          setSearchedValue(val);
          setFilters({
            ...filters,
            query: val
          });
        }}
      />
      {searchedValue && (
        <Box mb={0.5} display="flex" alignItems="center">
          <Typography color="secondary">Showing results for "{searchedValue}"</Typography>
          <Button
            id="clear-search"
            size="small"
            startIcon={<Close />}
            onClick={() => {
              setSearchValue('');
              setSearchedValue('');
              setPage(0);
              setFilters({
                query: ''
              });
              fetchFieldWalkthroughManagementLocations();
            }}
          >
            Clear Search
          </Button>
        </Box>
      )}
      <Table
        columns={columns}
        data={data}
        isLoading={isLoading}
        hidePagination
        mobileProps={{
          fields: columns
            .filter(c => c.Header)
            .map(c => ({
              name: c.Header,
              accessor: c.accessor,
              Cell: c?.Cell
            }))
        }}
        ResponsiveComponent={isMobile ? MobileTable : undefined}
      />
      {!isLoading && !isRefetching && (
        <Pagination
          page={page}
          count={recordCount}
          rowsPerPage={perPage}
          setPage={setPage}
          setRowsPerPage={setPerPage}
        />
      )}
      <AddLocationModal
        open={isAddModalOpen}
        onClose={() => {
          setIsAddModalOpen(false);
          fetchFieldWalkthroughManagementLocations();
        }}
      />
    </>
  );
};
